import { useState, useEffect } from "react";
import {
  ChevronRight,
  ChevronDown,
  Circle,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";

export interface ControlNode {
  id: string;
  label?: string; // display badge text, falls back to id if not set
  title: string;
  children?: ControlNode[];
  status?: "pass" | "fail" | "partial" | "untested";
  selected?: boolean;
  hasParams?: boolean;
}

interface ControlTreeViewProps {
  nodes: ControlNode[];
  onSelect?: (node: ControlNode) => void;
  selectedId?: string;
  selectable?: boolean;
  checkable?: boolean;
  checkedIds?: Set<string>;
  onCheck?: (id: string, checked: boolean) => void;
  hideParamsBadge?: boolean;
  /** When true all nodes expand, when false all collapse. undefined = no override. */
  expandAll?: boolean;
  /** Bump this to re-trigger expandAll even if the boolean hasn't changed. */
  expandTrigger?: number;
}

const statusIcon = {
  pass: <CheckCircle2 className="size-3.5 text-emerald-500 flex-shrink-0" />,
  fail: <XCircle className="size-3.5 text-red-500 flex-shrink-0" />,
  partial: <AlertCircle className="size-3.5 text-amber-500 flex-shrink-0" />,
  untested: <Circle className="size-3.5 d flex-shrink-0" />,
};

/** Count all leaf (non-group) nodes recursively */
function countLeaves(node: ControlNode): number {
  if (!node.children || node.children.length === 0) return 1;
  return node.children.reduce((sum, child) => sum + countLeaves(child), 0);
}

/** Collect all leaf node IDs under a node */
function collectLeafIds(node: ControlNode): string[] {
  if (!node.children || node.children.length === 0) return [node.id];
  return node.children.flatMap(collectLeafIds);
}

interface TreeNodeProps extends ControlTreeViewProps {
  node: ControlNode;
  depth: number;
}

function TreeNode({
  node,
  depth,
  onSelect,
  selectedId,
  selectable,
  checkable,
  checkedIds,
  onCheck,
  hideParamsBadge,
  expandAll,
  expandTrigger,
}: TreeNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;
  const isGroup = depth === 0 && hasChildren;

  // For groups: derive checked state from children leaves
  const leafIds = hasChildren ? collectLeafIds(node) : [];
  const checkedLeafCount = hasChildren
    ? leafIds.filter((id) => checkedIds?.has(id)).length
    : 0;
  const isChecked = hasChildren
    ? checkedLeafCount === leafIds.length && leafIds.length > 0
    : (checkedIds?.has(node.id) ?? false);
  const isIndeterminate =
    hasChildren && checkedLeafCount > 0 && checkedLeafCount < leafIds.length;

  // Sync with expandAll prop (trigger changes force re-apply)
  useEffect(() => {
    if (expandAll !== undefined) {
      setExpanded(expandAll);
    }
  }, [expandAll, expandTrigger]);

  // ── Group node (depth 0 with children) - rendered as bordered accordion card ──
  if (isGroup) {
    const leafCount = countLeaves(node);
    return (
      <div className="rounded-md border border-border overflow-hidden">
        {/* Group header */}
        <button
          onClick={() => {
            setExpanded((prev) => !prev);
            if (selectable) onSelect?.(node);
          }}
          className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${
            isSelected
              ? "bg-indigo-50 dark:bg-indigo-500/10"
              : "bg-muted/40 hover:bg-muted/60"
          }`}
        >
          {expanded ? (
            <ChevronDown className="size-3.5 d shrink-0" />
          ) : (
            <ChevronRight className="size-3.5 d shrink-0" />
          )}

          {checkable && (
            <input
              type="checkbox"
              checked={isChecked}
              ref={(el) => {
                if (el) el.indeterminate = !!isIndeterminate;
              }}
              onChange={(e) => {
                e.stopPropagation();
                onCheck?.(node.id, e.target.checked);
              }}
              className="size-3.5 flex-shrink-0 accent-foreground"
              disabled={!onCheck}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {node.status && statusIcon[node.status]}

          <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 shrink-0">
            {node.label ?? node.id}
          </span>
          <span className="text-xs font-semibold truncate">{node.title}</span>
          <span className="ml-auto text-[10px] d shrink-0">
            {checkable ? `${checkedLeafCount} / ${leafCount}` : leafCount}
          </span>
        </button>

        {/* Children */}
        {expanded && (
          <div className="divide-y divide-border">
            {node.children!.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                onSelect={onSelect}
                selectedId={selectedId}
                selectable={selectable}
                checkable={checkable}
                checkedIds={checkedIds}
                onCheck={onCheck}
                hideParamsBadge={hideParamsBadge}
                expandAll={expandAll}
                expandTrigger={expandTrigger}
                nodes={[]}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Leaf / nested child node ──
  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 text-sm transition-colors cursor-pointer ${
          isSelected
            ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200"
            : "hover:bg-accent"
        }`}
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: "12px" }}
        onClick={() => {
          if (hasChildren) setExpanded((prev) => !prev);
          if (selectable) onSelect?.(node);
        }}
      >
        {/* Expand toggle for nested groups */}
        {hasChildren ? (
          <span
            className="flex-shrink-0 size-4 flex items-center justify-center cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            {expanded ? (
              <ChevronDown className="size-3.5 d" />
            ) : (
              <ChevronRight className="size-3.5 d" />
            )}
          </span>
        ) : (
          <span className="flex-shrink-0 size-4" />
        )}

        {checkable && (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => {
              e.stopPropagation();
              onCheck?.(node.id, e.target.checked);
            }}
            className="size-3.5 flex-shrink-0 accent-foreground"
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {node.status && statusIcon[node.status]}

        <span
          className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
            isSelected
              ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
              : "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400"
          }`}
        >
          {node.label ?? node.id}
        </span>
        <span className="text-xs truncate">{node.title}</span>
        {node.hasParams && !hideParamsBadge && (
          <span
            className={`ml-auto flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
              isSelected
                ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                : "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400"
            }`}
          >
            Params
          </span>
        )}
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children!.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              selectable={selectable}
              checkable={checkable}
              checkedIds={checkedIds}
              onCheck={onCheck}
              hideParamsBadge={hideParamsBadge}
              expandAll={expandAll}
              nodes={[]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ControlTreeView(props: ControlTreeViewProps) {
  return (
    <div className="select-none space-y-1">
      {props.nodes.map((node) => (
        <TreeNode key={node.id} {...props} node={node} depth={0} />
      ))}
    </div>
  );
}
